import { FIELD_LABEL_CLASS } from '@atlas/ui-components'
import { cn, Input, Label } from '@atlas/ui-primitives'
import { ClassifyPicker, facetsToRailAxes } from '@/components/classification/ClassifyPicker'
import type { Facet } from '@/domain/library.types'
import { RULE_LIFECYCLE_LABEL, type Rule, RuleLifecycle } from '@/domain/rules.types'
import { AppliesToPicker } from './AppliesToPicker'
import { RuleTypeSeverityFields } from './RuleTypeSeverityFields'
import { RuleValidationFields } from './RuleValidationFields'

/**
 * The rule fields a user can edit. `lifecycle` is included so a draft can be activated
 * here — but only draft ⇄ active. Retiring stays on the dedicated Archive action, which
 * confirms first, since it removes the rule from every future resolution.
 */
export type RuleEditValue = Omit<Rule, 'id' | 'createdAt'>

export interface EditRulePropertiesFormProps {
	value: RuleEditValue
	facets: Facet[]
	onChange: (next: RuleEditValue) => void
}

const fieldLabel = `mb-1.5 block ${FIELD_LABEL_CLASS}`
const inputCls = 'w-full rounded-ds-md border border-ds-border bg-ds-background px-3 py-2 text-sm text-ds-text'
const selectCls = 'h-9 w-full rounded-ds-md border border-ds-border bg-ds-background px-2 text-sm text-ds-text'

/**
 * Draft ⇄ active only. Retiring is the Archive action's job, so a retired rule shows
 * its state read-only here rather than offering a revive path this form doesn't own.
 *
 * The API rejects activating a rule with an empty check definition, so the control is
 * disabled in that case and says why — a user should not have to submit to find out.
 */
function LifecycleField({ value, onChange }: Pick<EditRulePropertiesFormProps, 'value' | 'onChange'>) {
	const retired = value.lifecycle === RuleLifecycle.RETIRED
	const canActivate = value.checkDefinition.trim().length > 0
	const blocked = !canActivate && value.lifecycle === RuleLifecycle.DRAFT

	if (retired) {
		return (
			<div>
				<span className={fieldLabel}>Lifecycle</span>
				<p className="text-sm text-ds-text">Retired</p>
				<p className="mt-1 text-xs text-ds-text-muted">
					A retired rule never resolves. Restoring it is not available here.
				</p>
			</div>
		)
	}

	return (
		<div>
			<Label htmlFor="edit-lifecycle" className={fieldLabel}>
				Lifecycle
			</Label>
			<select
				id="edit-lifecycle"
				value={value.lifecycle}
				disabled={blocked}
				onChange={(e) => onChange({ ...value, lifecycle: e.target.value as RuleLifecycle })}
				className={cn(selectCls, blocked && 'opacity-60')}
			>
				<option value={RuleLifecycle.DRAFT}>{RULE_LIFECYCLE_LABEL[RuleLifecycle.DRAFT]}</option>
				<option value={RuleLifecycle.ACTIVE}>{RULE_LIFECYCLE_LABEL[RuleLifecycle.ACTIVE]}</option>
			</select>
			<p className="mt-1 text-xs text-ds-text-muted">
				{blocked
					? 'Add a check definition to activate this rule.'
					: 'An active rule resolves for matching content; a draft never does.'}
			</p>
		</div>
	)
}

function ValidationAndDatesFields({
	value,
	facets,
	onChange,
}: Pick<EditRulePropertiesFormProps, 'value' | 'facets' | 'onChange'>) {
	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
			<RuleValidationFields
				validationMethod={value.validationMethod}
				metadataCheck={value.metadataCheck ?? null}
				facets={facets}
				idPrefix="edit"
				onChange={(next) => onChange({ ...value, ...next })}
			/>
			<div>
				<Label htmlFor="edit-effective-from" className={fieldLabel}>
					Effective from
				</Label>
				<Input
					id="edit-effective-from"
					type="date"
					value={value.effectiveFrom}
					onChange={(e) => onChange({ ...value, effectiveFrom: e.target.value })}
				/>
			</div>
			<div>
				<Label htmlFor="edit-effective-to" className={fieldLabel}>
					Effective to
				</Label>
				<Input
					id="edit-effective-to"
					type="date"
					value={value.effectiveTo ?? ''}
					onChange={(e) => onChange({ ...value, effectiveTo: e.target.value || null })}
				/>
			</div>
		</div>
	)
}

/**
 * Editable rule properties (detail page, edit mode): statement, type, severity,
 * classification, check definition, validation method, effective dates. Controlled;
 * the page owns Save/Cancel + the confirm-before-save step. Presentational + storied.
 */
export function EditRulePropertiesForm({ value, facets, onChange }: EditRulePropertiesFormProps) {
	return (
		<div className="space-y-4">
			<div>
				<Label htmlFor="edit-statement" className={fieldLabel}>
					Statement
				</Label>
				<textarea
					id="edit-statement"
					required
					rows={2}
					value={value.statement}
					onChange={(e) => onChange({ ...value, statement: e.target.value })}
					className={inputCls}
				/>
			</div>

			<RuleTypeSeverityFields
				type={value.type}
				severity={value.severity}
				idPrefix="edit"
				onChange={(next) => onChange({ ...value, ...next })}
			/>

			<div>
				<span className={fieldLabel}>Classify — applies to (empty = all)</span>
				<ClassifyPicker
					value={value.classification}
					axes={facetsToRailAxes(facets)}
					onChange={(classification) => onChange({ ...value, classification })}
				/>
			</div>

			<div>
				<span className={fieldLabel}>Governs element types (empty = all)</span>
				<AppliesToPicker
					value={value.appliesTo}
					onChange={(appliesTo) => onChange({ ...value, appliesTo })}
					idPrefix="edit-applies-to"
				/>
			</div>

			<div>
				<Label htmlFor="edit-check-definition" className={fieldLabel}>
					Check definition
				</Label>
				<textarea
					id="edit-check-definition"
					rows={2}
					value={value.checkDefinition}
					onChange={(e) => onChange({ ...value, checkDefinition: e.target.value })}
					className={inputCls}
				/>
			</div>

			<ValidationAndDatesFields value={value} facets={facets} onChange={onChange} />

			<LifecycleField value={value} onChange={onChange} />
		</div>
	)
}
